/*
 * Investment Dropdown
 * */

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import { MenuItem } from '@mui/material';
import Typography from '@mui/material/Typography';

import SimplifiStyledSelect from 'components/SimplifiSelects/SimplifiStyledSelect';

import MultiSelectBottomButtons from './MultiSelectBottomButtons';
import useStyles from './styles';

const allItemsKey = '0B2F7296-D641-45DC-BBD0-14168635D69E';

const SimplifiMultiSelect = (props) => {
  const { classes, cx } = useStyles();
  const cancelFocus = useRef(null);
  const {
    menuArray,
    onSelected,
    name = 'Accounts',
    selectedItems,
    showAll,
    startDroppedDown = true,
    instantFilter,
    setIsAllSelected,
    menuTopContent,
    menuTopContentSticky = false,
    menuTopContentDivider = false,
    useCustomMenuLayout = false,
    ...other
  } = props;

  const [mySelectedItems, setMySelectedItems] = useState(selectedItems || []);
  const [mySavedSelectedItems, setMySavedSelectedItems] = useState(selectedItems || []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [shownItems, setShownItems] = useState(new Array(menuArray.length).fill(startDroppedDown));
  const [headerLabels, setHeaderLabels] = useState(menuArray.map((item) => item.name));
  const [itemArray, setItemArray] = useState(menuArray.map((item) => item.items));

  const open = Boolean(anchorEl);

  const getItemArrayLength = useCallback(
    () => itemArray.reduce((total, items) => total + (items?.length || 0), 0),
    [itemArray],
  );

  useEffect(() => {
    setShownItems(new Array(menuArray.length).fill(startDroppedDown));
  }, [menuArray.length, startDroppedDown]);

  useEffect(() => {
    setHeaderLabels(menuArray.map((item) => item.name));
    setItemArray(menuArray.map((item) => item.items));
  }, [menuArray]);

  useEffect(() => {
    if (selectedItems) {
      setMySavedSelectedItems(selectedItems);
      setMySelectedItems(selectedItems);
    } else {
      setMySavedSelectedItems([]);
      setMySelectedItems([]);
    }
  }, [selectedItems]);

  const containsSomeItems = useCallback(
    (items) => items.some((item) => mySelectedItems.indexOf(item) >= 0),
    [mySelectedItems],
  );

  const containsAllItems = useCallback(
    (items) => {
      for (let index = 0; index < items.length; index += 1) {
        if (!mySelectedItems.includes(items[index])) {
          return false;
        }
      }
      return true;
    },
    [mySelectedItems],
  );

  const getItemsInSingleArray = useMemo(() => itemArray.flatMap((items) => items || []), [itemArray]);

  const syncIsAllSelected = useCallback(
    (nextSelectedItems) => {
      if (!setIsAllSelected) {
        return;
      }

      setIsAllSelected(nextSelectedItems.length === getItemArrayLength());
    },
    [getItemArrayLength, setIsAllSelected],
  );

  const applyFilter = useCallback(
    (event) => {
      event?.stopPropagation?.();
      onSelected(mySelectedItems);
      setMySavedSelectedItems(mySelectedItems);
      syncIsAllSelected(mySelectedItems);
      setAnchorEl(null);
    },
    [mySelectedItems, onSelected, syncIsAllSelected],
  );

  const cancelFilter = useCallback(
    (event) => {
      if (event?.key !== 'Tab') {
        event?.stopPropagation?.();
        setAnchorEl(null);
        setMySelectedItems(mySavedSelectedItems);
      } else if (cancelFocus.current !== document.activeElement) {
        cancelFocus.current?.focus();
      }
    },
    [mySavedSelectedItems],
  );

  const removeUndefined = useCallback((itemList) => itemList.filter((item) => item !== undefined), []);

  const eraseDuplicates = useCallback((itemList) => {
    const dedupedItems = itemList.filter(
      (item, index, self) => index === self.findIndex((duplicate) => duplicate.id === item.id),
    );

    return dedupedItems;
  }, []);

  const setSelection = useCallback(
    (nextSelectedItems) => {
      const normalizedItems = eraseDuplicates(removeUndefined(nextSelectedItems));
      setMySelectedItems(normalizedItems);

      if (typeof onSelected !== 'undefined' && instantFilter) {
        onSelected(normalizedItems);
      }
    },
    [eraseDuplicates, instantFilter, onSelected, removeUndefined],
  );

  const toggleAllItems = useCallback(() => {
    if (mySelectedItems.length >= getItemArrayLength()) {
      setSelection([]);
    } else {
      setSelection(getItemsInSingleArray);
    }
  }, [getItemArrayLength, getItemsInSingleArray, mySelectedItems.length, setSelection]);

  const toggleHeaderItems = useCallback(
    (items) => {
      if (containsAllItems(items)) {
        setSelection(mySelectedItems.filter((item) => !items.includes(item)));
      } else {
        setSelection(mySelectedItems.concat(items));
      }
    },
    [containsAllItems, mySelectedItems, setSelection],
  );

  const toggleSingleItem = useCallback(
    (item) => {
      if (mySelectedItems.includes(item)) {
        setSelection(mySelectedItems.filter((selectedItem) => selectedItem !== item));
      } else {
        setSelection(mySelectedItems.concat(item));
      }
    },
    [mySelectedItems, setSelection],
  );

  const onAccountChange = useCallback(
    (event) => {
      let newSelectedItems = removeUndefined(event.target.value);

      if (newSelectedItems.includes(allItemsKey)) {
        if (mySelectedItems.length >= getItemArrayLength()) {
          newSelectedItems = [];
        } else {
          newSelectedItems = getItemsInSingleArray;
        }
      } else if (newSelectedItems) {
        for (let headerIndex = 0; headerIndex < headerLabels.length; headerIndex += 1) {
          if (newSelectedItems.includes(headerLabels[headerIndex])) {
            for (let selectedIndex = newSelectedItems.length - 1; selectedIndex >= 0; selectedIndex -= 1) {
              if (headerLabels.includes(newSelectedItems[selectedIndex])) {
                newSelectedItems.splice(selectedIndex, 1);
              }
            }

            if (containsAllItems(itemArray[headerIndex])) {
              for (let itemIndex = newSelectedItems.length - 1; itemIndex >= 0; itemIndex -= 1) {
                if (itemArray[headerIndex].includes(newSelectedItems[itemIndex])) {
                  newSelectedItems.splice(itemIndex, 1);
                }
              }
            } else {
              newSelectedItems = newSelectedItems.concat(itemArray[headerIndex]);
            }
          }
        }
      } else {
        newSelectedItems = [];
      }

      setSelection(newSelectedItems);
    },
    [
      containsAllItems,
      getItemArrayLength,
      getItemsInSingleArray,
      headerLabels,
      itemArray,
      mySelectedItems.length,
      removeUndefined,
      setSelection,
    ],
  );

  const accountRenderValue = useCallback(
    (renderSelectedItems) => {
      const tempSelectedItems = removeUndefined(renderSelectedItems);

      if (tempSelectedItems.length === 1) {
        return `${tempSelectedItems[0].nickname || tempSelectedItems[0].name}${'isClosed' in tempSelectedItems[0] && tempSelectedItems[0].isClosed ? ' (Closed)' : ''}`;
      }

      if (tempSelectedItems.length === getItemArrayLength()) {
        return 'All accounts';
      }

      if (tempSelectedItems.length !== 0) {
        return (
          <div className={classes.textContainer}>
            <div className={classes.numItems}>
              <Typography className={classes.innerNum}>{tempSelectedItems.length}</Typography>
            </div>
            <Typography className={classes.textBox}>{name.toUpperCase()}</Typography>
          </div>
        );
      }

      return 'None selected';
    },
    [classes, getItemArrayLength, name, removeUndefined],
  );

  const menuProps = useMemo(
    () => ({
      classes: {
        paper: classNames(classes.menuPaper, anchorEl ? '' : 'closed'),
        list: classes.menuList,
      },
      disableAutoFocusItem: true,
    }),
    [anchorEl, classes],
  );

  const renderItemLabel = useCallback(
    (item) => (
      <Typography className={classes.accountNamePrimary} color='textSecondary'>
        <span className={classes.accountNameValue}>{item.nickname || item.name}</span>
        {'isClosed' in item && item.isClosed ? (
          <Typography component='span' variant='captionRegular' className={classes.closedAccountLabel}>
            (Closed)
          </Typography>
        ) : null}
      </Typography>
    ),
    [classes],
  );

  const renderTopContent = useCallback(
    (dividerClassName) => {
      if (!menuTopContent) {
        return null;
      }

      return (
        <Box className={cx(classes.menuTopContentItem, menuTopContentSticky && classes.menuTopContentSticky)}>
          {menuTopContent}
          {menuTopContentDivider ? <Divider className={dividerClassName} /> : null}
        </Box>
      );
    },
    [classes, cx, menuTopContent, menuTopContentDivider, menuTopContentSticky],
  );

  const renderLegacyAllItem = useCallback(
    () =>
      showAll &&
      itemArray &&
      getItemArrayLength() > 1 && (
        <MenuItem
          key={allItemsKey}
          value={allItemsKey}
          id={name}
          classes={{ selected: classes.selectedMenuItem }}
          className={classes.allAccountsPadding}
        >
          <IconButton
            id='all-toggle'
            aria-label='Expand or Collapse'
            className={classes.dropdownIconAllAccts}
            key='all-toggle'
            disabled
            onClick={(event) => {
              event?.stopPropagation();
              setShownItems([]);
            }}
            size='large'
          >
            {shownItems ? <ArrowDropDownRoundedIcon /> : <ArrowRightRoundedIcon />}
          </IconButton>
          <Checkbox
            checked={mySelectedItems.length === getItemArrayLength()}
            color='primary'
            indeterminate={Boolean(mySelectedItems.length && mySelectedItems.length !== getItemArrayLength())}
          />
          <ListItemText primary={`All ${name}`} />
        </MenuItem>
      ),
    [classes, getItemArrayLength, itemArray, mySelectedItems.length, name, showAll, shownItems],
  );

  const renderLegacyMenuItems = useCallback(
    () =>
      itemArray.map((list, index) => {
        const items = [];

        const subMenuItems = list.map((item) => (
          <MenuItem
            key={item.id || item.nickname || item.name}
            value={item}
            className={classes.subMenuItem}
            classes={{ selected: classes.selectedMenuItem }}
          >
            <Checkbox checked={mySelectedItems.includes(item)} color='primary' />
            <ListItemText
              id={`${item.id}-item-${item.nickname || item.name}`}
              className={classes.accountListItemText}
              primary={renderItemLabel(item)}
            />
          </MenuItem>
        ));

        const parentMenuItem = (
          <MenuItem
            key={`header-${headerLabels[index]}`}
            value={headerLabels[index]}
            className={classes.headerMenuItem}
            classes={{ selected: classes.selectedMenuItem }}
          >
            <IconButton
              id={`${headerLabels[index]}-toggle`}
              aria-label='Expand or Collapse'
              className={classes.dropdownIcon}
              key={`${headerLabels[index]}-toggle`}
              onClick={(event) => {
                event?.stopPropagation();
                setShownItems((previousState) => {
                  const nextState = [...previousState];
                  nextState.splice(index, 1, !nextState[index]);
                  return nextState;
                });
              }}
              size='large'
            >
              {shownItems[index] ? <ArrowDropDownRoundedIcon /> : <ArrowRightRoundedIcon />}
            </IconButton>
            <Checkbox
              checked={containsAllItems(itemArray[index])}
              color='primary'
              indeterminate={!containsAllItems(itemArray[index]) && Boolean(containsSomeItems(itemArray[index]))}
            />
            <ListItemText id={`item-${index}-text`} primary={headerLabels[index]} />
          </MenuItem>
        );

        if (list?.length) {
          items.push(parentMenuItem);

          if (shownItems[index]) {
            items.push(subMenuItems);
            if (index !== itemArray.length - 1) {
              items.push(<Divider key={`divider-${headerLabels[index]}`} className={classes.divider} />);
            }
          }
        }

        return items;
      }),
    [
      classes,
      containsAllItems,
      containsSomeItems,
      headerLabels,
      itemArray,
      mySelectedItems,
      renderItemLabel,
      shownItems,
    ],
  );

  const renderCustomButtonRow = useCallback(
    (item, checked, onClick, className) => (
      <button
        key={item.id || item.nickname || item.name}
        type='button'
        className={cx(classes.customMenuItem, className)}
        onClick={onClick}
      >
        <Checkbox checked={checked} color='primary' tabIndex={-1} />
        <ListItemText
          id={`${item.id}-item-${item.nickname || item.name}`}
          className={classes.accountListItemText}
          primary={renderItemLabel(item)}
        />
      </button>
    ),
    [classes, cx, renderItemLabel],
  );

  const renderCustomMenuItems = useCallback(() => {
    const items = [];

    if (showAll && getItemArrayLength() > 1) {
      items.push(
        <button
          key={allItemsKey}
          type='button'
          className={cx(classes.customMenuItem, classes.customAllAccountsPadding)}
          onClick={toggleAllItems}
        >
          <IconButton
            id='all-toggle-custom'
            aria-label='Expand or Collapse'
            className={classes.dropdownIconAllAccts}
            disabled
            size='large'
          >
            <ArrowDropDownRoundedIcon />
          </IconButton>
          <Checkbox
            checked={mySelectedItems.length === getItemArrayLength()}
            color='primary'
            indeterminate={Boolean(mySelectedItems.length && mySelectedItems.length !== getItemArrayLength())}
            tabIndex={-1}
          />
          <ListItemText primary={`All ${name}`} />
        </button>,
      );
    }

    itemArray.forEach((list, index) => {
      if (!list?.length) {
        return;
      }

      items.push(
        <button
          key={`header-${headerLabels[index]}`}
          type='button'
          className={cx(classes.customMenuItem, classes.customHeaderMenuItem)}
          onClick={() => toggleHeaderItems(itemArray[index])}
        >
          <IconButton
            id={`${headerLabels[index]}-toggle`}
            aria-label='Expand or Collapse'
            className={classes.dropdownIcon}
            onClick={(event) => {
              event.stopPropagation();
              setShownItems((previousState) => {
                const nextState = [...previousState];
                nextState.splice(index, 1, !nextState[index]);
                return nextState;
              });
            }}
            size='large'
          >
            {shownItems[index] ? <ArrowDropDownRoundedIcon /> : <ArrowRightRoundedIcon />}
          </IconButton>
          <Checkbox
            checked={containsAllItems(itemArray[index])}
            color='primary'
            indeterminate={!containsAllItems(itemArray[index]) && Boolean(containsSomeItems(itemArray[index]))}
            tabIndex={-1}
          />
          <ListItemText id={`item-${index}-text`} primary={headerLabels[index]} />
        </button>,
      );

      if (shownItems[index]) {
        list.forEach((item) => {
          items.push(
            renderCustomButtonRow(
              item,
              mySelectedItems.includes(item),
              () => toggleSingleItem(item),
              classes.customSubMenuItem,
            ),
          );
        });

        if (index !== itemArray.length - 1) {
          items.push(<Divider key={`divider-${headerLabels[index]}`} className={classes.divider} />);
        }
      }
    });

    return items;
  }, [
    classes,
    containsAllItems,
    containsSomeItems,
    cx,
    getItemArrayLength,
    headerLabels,
    itemArray,
    mySelectedItems,
    name,
    renderCustomButtonRow,
    showAll,
    shownItems,
    toggleAllItems,
    toggleHeaderItems,
    toggleSingleItem,
  ]);

  const customPopupContent = useMemo(
    () => (
      <Box className={classes.customMenuRoot}>
        {renderTopContent(classes.customMenuTopContentDivider) ? (
          <Box className={classes.customMenuTopContent}>{renderTopContent(classes.customMenuTopContentDivider)}</Box>
        ) : null}
        <Box className={classes.customMenuListContainer}>{renderCustomMenuItems()}</Box>
        <MultiSelectBottomButtons
          sticky={false}
          className={classes.customMenuFooter}
          mySelectedItems={mySelectedItems}
          cancelFocus={cancelFocus}
          cancelFilter={cancelFilter}
          applyFilter={applyFilter}
        />
      </Box>
    ),
    [applyFilter, cancelFilter, classes, mySelectedItems, renderCustomMenuItems, renderTopContent],
  );

  return (
    <SimplifiStyledSelect
      showZeroState={!menuArray.length}
      onOpen={(event) => {
        setAnchorEl(event.currentTarget);
      }}
      MenuProps={menuProps}
      id='account-select-dropdown'
      open={open}
      anchorEl={anchorEl}
      multiple
      value={mySelectedItems}
      onClose={cancelFilter}
      onChange={onAccountChange}
      displayEmpty
      renderValue={(renderSelectedItems) => accountRenderValue(renderSelectedItems)}
      customPopupContent={useCustomMenuLayout ? customPopupContent : undefined}
      customPopoverProps={
        useCustomMenuLayout
          ? {
              PaperProps: {
                className: classes.customMenuPaper,
              },
            }
          : undefined
      }
      {...other}
    >
      {renderTopContent(classes.menuTopContentDivider)}
      {renderLegacyAllItem()}
      {renderLegacyMenuItems()}
      <MultiSelectBottomButtons
        mySelectedItems={mySelectedItems}
        cancelFocus={cancelFocus}
        cancelFilter={cancelFilter}
        applyFilter={applyFilter}
      />
    </SimplifiStyledSelect>
  );
};

SimplifiMultiSelect.propTypes = {
  instantFilter: PropTypes.bool,
  menuArray: PropTypes.array,
  menuTopContent: PropTypes.node,
  menuTopContentDivider: PropTypes.bool,
  menuTopContentSticky: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onSelected: PropTypes.func.isRequired,
  selectedItems: PropTypes.array,
  setIsAllSelected: PropTypes.func,
  sharedcomponentid: PropTypes.string,
  showAll: PropTypes.bool,
  startDroppedDown: PropTypes.bool,
  useCustomMenuLayout: PropTypes.bool,
  viewState: PropTypes.string,
};

export default SimplifiMultiSelect;
